import { templatesForTopic } from '../content/templates';
import type { Rng, TopicId } from '../content/types';

/**
 * Leitner boxes, 1 to 5.
 *
 * Box 1 is "just got this wrong or have never seen it", box 5 is "solid".
 * A right answer promotes by one, a wrong answer drops straight back to 1 —
 * the harsh demotion is the point of the system, because a skill you just
 * missed is exactly the one worth seeing again soon.
 */
export const MIN_BOX = 1;
export const MAX_BOX = 5;

export function promote(box: number | undefined): number {
  return Math.min(MAX_BOX, (box ?? MIN_BOX) + 1);
}

export function demote(): number {
  return MIN_BOX;
}

export interface ScheduleInput {
  readonly boxes: Readonly<Record<string, number>>;
  /** Template ids she parked, newest last. These jump the queue. */
  readonly parked: readonly string[];
}

/**
 * Pick the templates for a set.
 *
 * Priority, highest first:
 *
 *  1. Anything parked in this topic. "It comes back tomorrow" has to be true
 *     or parking is just a dressed-up skip, and the whole no-penalty promise
 *     stops meaning anything.
 *  2. Templates never attempted, so a new topic opens up rather than drilling
 *     the same two questions.
 *  3. Everything else, lowest box first — weakest skill gets the most air time.
 *
 * Ties are shuffled so a set is never the same running order twice.
 */
export function chooseTemplates(
  topicId: TopicId,
  count: number,
  schedule: ScheduleInput,
  rng: Rng,
): string[] {
  const available = templatesForTopic(topicId).map((t) => t.id);
  if (available.length === 0) throw new Error(`No templates for topic ${topicId}`);

  const parkedHere = new Set(schedule.parked.filter((id) => available.includes(id)));

  const rank = (id: string): number => {
    if (parkedHere.has(id)) return 0;
    const box = schedule.boxes[id];
    if (box === undefined) return 1;
    return 1 + box; // box 1 -> 2, box 5 -> 6
  };

  // Shuffle first, then sort — a stable sort preserves the shuffled order
  // within each rank, which is what breaks the ties randomly.
  const ordered = rng.shuffle(available).sort((a, b) => rank(a) - rank(b));

  const picked: string[] = [];
  while (picked.length < count) {
    const remaining = count - picked.length;
    // Re-cycle the whole ordered list when a short topic runs out, so every
    // template is used once before any of them repeats.
    picked.push(...ordered.slice(0, Math.min(remaining, ordered.length)));
  }
  return picked;
}
